import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Settings,
  ShieldUser,
  ChartColumnIncreasing,
  Database,
  Factory,
  Landmark,
  LayoutDashboard,
  FileBarChart,
  LineChart,
  Wallet,
  Sun,
  UsersRound,
  BookOpenCheck,
  ChevronsLeft,
  ChevronsRight,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNewNavItems } from '../../hooks/useNewNavItems';
import { IconButton } from '../UI/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../UI/tooltip';
import { PRE_CANNED_REPORTS_ENABLED } from '../../config/featureFlags';

interface SidebarProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
  collapsed?: boolean;
  onNavigate?: () => void;
  onToggle?: () => void;
  isMobileViewport?: boolean;
}

interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
}

const ENABLE_ALLOCATION_ACCESS_RESTRICTION = true;

// Blinking dot for tabs the user has not opened yet (see useNewNavItems).
const NewDot: React.FC = () => (
  <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
    <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 motion-safe:animate-ping" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
  </span>
);

// Visible navigation surface (top section). Permission-backed entries are
// gated on `<slug>.access`.
const getMenuItems = (
  canAccessModule: (slug: string) => boolean,
  isAdmin: () => boolean,
) => {
  const items: MenuItem[] = [];
  const canAccessSmartInsights = canAccessModule('smart_insights_bot');

  if (PRE_CANNED_REPORTS_ENABLED) {
    items.push({ id: 'pre-canned-reports', label: 'SmartMorning', icon: Sun, path: '/smart-morning-for-leaders' });
  }

  items.push({ id: 'labor-performance', label: 'Labor Performance', icon: UsersRound, path: '/labor-performance' });

  if (isAdmin()) {
    items.push({ id: 'smart-morning', label: 'SmartMorning', icon: FileBarChart, path: '/smart-morning' });
  }

  if (canAccessSmartInsights || canAccessModule('sac_wow_dashboard')) {
    items.push({ id: 'smart-dashboards', label: 'SmartDashboards', icon: LayoutDashboard, path: '/smart-dashboards' });
  }

  if (canAccessSmartInsights) {
    items.push({ id: 'finance-v2', label: 'SmartInsights', icon: ChartColumnIncreasing, path: '/smart-insights' });
  }

  if (!ENABLE_ALLOCATION_ACCESS_RESTRICTION || canAccessModule('allocation')) {
    items.push({ id: 'allocations', label: 'Allocations', icon: Wallet, path: '/allocations' });
  }

  if (canAccessModule('redzone_analysis')) {
    items.push({ id: 'redzone-analysis', label: 'Redzone Analysis', icon: Factory, path: '/redzone-analysis' });
  }

  // Labelled "SmartInsights V3" in the UI; backend module/route keep the smart_finance name.
  if (canAccessModule('smart_finance')) {
    items.push({ id: 'smart-finance', label: 'SmartInsights V3', icon: Landmark, path: '/smart-finance' });
  }

  return items;
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onNavigate,
  onToggle,
  isMobileViewport = false,
}) => {
  const location = useLocation();
  const { canAccessModule, isAdmin, user } = useAuth();

  const getCurrentPage = () => {
    const path = location.pathname;
    
    if (path === '/notifications') return 'notifications';
    if (path === '/users') return 'users';
    if (path === '/adoption' || path.startsWith('/adoption/')) return 'adoption';
    if (path === '/settings') return 'settings';
    if (path === '/evaluations') return 'evaluations';
    if (path === '/smart-insights' || path.startsWith('/smart-insights/')) return 'finance-v2';
    if (path === '/smart-morning-leaders' || path.startsWith('/smart-morning-leaders/')) return 'pre-canned-reports';
    if (path === '/smart-morning-for-leaders' || path.startsWith('/smart-morning-for-leaders/')) return 'pre-canned-reports';
    if (path === '/pre-canned-reports' || path.startsWith('/pre-canned-reports/')) return 'pre-canned-reports';
    if (path === '/labor-performance' || path.startsWith('/labor-performance/') || path === '/smart-morning/labor-performance') return 'labor-performance';
    if (path === '/sap-intelligence' || path.startsWith('/sap-intelligence/')) return 'sap-intelligence';
    if (path === '/smart-dashboards' || path.startsWith('/smart-dashboards/') || path === '/wow-dashboard' || path.startsWith('/wow-dashboard/')) return 'smart-dashboards';
    if (path === '/smart-morning' || path.startsWith('/smart-morning/')) return 'smart-morning';
    if (path === '/allocations' || path.startsWith('/allocations/')) return 'allocations';
    if (path === '/redzone-analysis' || path.startsWith('/redzone-analysis/')) return 'redzone-analysis';
    if (path === '/smart-finance' || path.startsWith('/smart-finance/')) return 'smart-finance';
    return 'finance-v2';
  };

  const currentPage = getCurrentPage();
  const isNewItem = useNewNavItems(user?.id, currentPage);
  const navItems: MenuItem[] = getMenuItems(canAccessModule, isAdmin);
  if (currentPage === 'evaluations') {
    navItems.push({ id: 'evaluations', label: 'Evaluations', icon: BookOpenCheck, path: '/evaluations' });
  }
  // Bottom-pinned utility entries: Users (super_admin only) above
  // Settings, both grouped together at the bottom-left of the sidebar.
  // Users is gated by the canonical admin authorization; Settings is
  // gated on `settings.access` (every role).
  const bottomItems: MenuItem[] = [];
  if (canAccessModule('sap_intelligence')) {
    bottomItems.push({ id: 'sap-intelligence', label: 'SAP Intelligence', icon: Database, path: '/sap-intelligence' });
  }
  if (isAdmin()) {
    bottomItems.push({ id: 'users', label: 'Users', icon: ShieldUser, path: '/users' });
  }
  // Adoption & health dashboard — super_admin only (matches AdminProtectedRoute).
  if (isAdmin()) {
    bottomItems.push({ id: 'adoption', label: 'Adoption', icon: LineChart, path: '/adoption' });
  }
  if (canAccessModule('settings')) {
    bottomItems.push({ id: 'settings', label: 'Settings', icon: Settings, path: '/settings' });
  }

  const renderNavItem = (item: MenuItem) => {
    const Icon = item.icon;
    const isActive = currentPage === item.id;
    const isNew = !isActive && isNewItem(item.id);
    const label = isNew ? `${item.label} (new)` : item.label;
    const navLink = (
      <Link
        key={item.id}
        to={item.path}
        onClick={onNavigate}
        aria-current={isActive ? 'page' : undefined}
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={`group relative flex w-full items-center rounded-lg font-medium transition-all duration-300 ease-in-out ${
          collapsed ? 'justify-center' : 'justify-start'
        } ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : isNew
              ? 'bg-primary/5 text-neutral-900 ring-1 ring-primary/30 hover:bg-accent hover:text-accent-foreground'
              : 'text-neutral-700 hover:bg-accent hover:text-accent-foreground'
        }`}
        style={{
          gap: collapsed ? '0' : '0.625rem',
          padding: collapsed ? '0.625rem' : '0.625rem 0.75rem',
          minHeight: '2.5rem',
          fontSize: '0.72rem',
          lineHeight: '1.35',
        }}
      >
        <Icon
          className="flex-shrink-0 transition-transform duration-300 ease-in-out group-hover:scale-105"
          style={{ width: '0.95rem', height: '0.95rem' }}
        />
        <span
          aria-hidden={collapsed}
          className="overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out"
          style={{
            maxWidth: collapsed ? 0 : '11rem',
            opacity: collapsed ? 0 : 1,
          }}
        >
          {item.label}
        </span>
        {isNew && !collapsed && (
          <span className="ml-auto inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-primary">
            <NewDot />
            New
          </span>
        )}
        {isNew && collapsed && (
          <span className="absolute right-1 top-1">
            <NewDot />
          </span>
        )}
      </Link>
    );

    if (!collapsed) {
      return navLink;
    }

    return (
      <Tooltip key={item.id}>
        <TooltipTrigger asChild>
          {navLink}
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {label}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <aside
      className="flex h-full flex-col bg-white border-r border-neutral-200 transition-[width,padding] duration-300 ease-in-out"
      style={{
        width: '100%',
        paddingTop: '0.875rem',
        paddingBottom: '0.875rem',
        paddingLeft: collapsed ? '0.625rem' : '0.875rem',
        paddingRight: collapsed ? '0.625rem' : '0.875rem',
      }}
    >
      <div
        className={`flex min-h-[2.25rem] items-center border-b border-neutral-200 pb-3 transition-all duration-300 ease-in-out ${
          collapsed ? 'justify-center' : 'justify-end'
        }`}
        style={{ marginBottom: '0.75rem', gap: '0.375rem' }}
      >
        {onToggle && (
          <IconButton
            icon={
              collapsed && !isMobileViewport
                ? <ChevronsRight className="transition-transform duration-300" />
                : <ChevronsLeft className="transition-transform duration-300" />
            }
            aria-label={
              isMobileViewport
                ? 'Close menu'
                : collapsed
                  ? 'Expand sidebar'
                  : 'Collapse sidebar'
            }
            onClick={onToggle}
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0 rounded-md border-0 bg-transparent p-0 text-neutral-500 shadow-none hover:bg-transparent hover:text-neutral-700"
          />
        )}
      </div>

      <nav
        className="flex flex-1 flex-col"
        style={{ gap: '0.375rem' }}
      >
        <div className="flex flex-col" style={{ gap: '0.375rem' }}>
          {collapsed ? (
            <TooltipProvider delayDuration={120}>
              {navItems.map(renderNavItem)}
            </TooltipProvider>
          ) : (
            navItems.map(renderNavItem)
          )}
        </div>
        {bottomItems.length > 0 && (
          <div
            className={`mt-auto flex flex-col border-t border-neutral-200 pt-3 ${
              collapsed ? 'items-center' : ''
            }`}
            style={{ gap: '0.375rem' }}
          >
            {collapsed ? (
              <TooltipProvider delayDuration={120}>
                {bottomItems.map(renderNavItem)}
              </TooltipProvider>
            ) : (
              <>
                {bottomItems.map(renderNavItem)}
              </>
            )}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
